import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Page from 'components/layout/Page';
import { Button } from 'components/shared/Button';
import { LoggedUserContext } from 'context/logged-user/loggeduser.context';
import { useI18n } from 'hooks/useI18n';
import parse, { Element, HTMLReactParserOptions } from 'html-react-parser';
import { useRouter } from 'next/dist/client/router';
import { WP_NextPrev } from 'pages/blog/[slug]';
import { useContext, useMemo, useRef, useState } from 'react';
import { WP_REST_API_Categories, WP_REST_API_Post } from 'wp-types';
import BlogFloatingCta from '../layout/BlogFloatingCta';
import {
  DrawerCloseButton,
  DrawerOverlay,
  IndexDrawer,
  IndexDrawerContent,
  IndexToggleButton,
  StyledContainer,
  StyledFloatingBannerContainer,
  StyledHeaderWrapper,
  StyledMainLayout,
  StyledPostContent,
  StyledPostContentWrapper,
  StyledPostIndexSticky,
  StyledPostIndexWrapper,
  StyledPostInfoFooter,
  StyledRecommendedPosts
} from './BlogPostComponent.styled';
import PostHeaderCard from './PostHeaderCard';
import { Anchor, PostIndex } from './PostIndex';
import { slugify } from 'utils/slugify';
import { ImageModal } from './imageModal';
import BlogShareModal from './BlogShareModal';
import { faList, faShare } from '@fortawesome/pro-light-svg-icons';
import { sortCategories } from '../utils/blogUtils';
import BlogRegisterCta from '../layout/BlogRegisterCta';
import BlogCta from '../layout/BlogCta';

type BlogPostComponentProps = {
  disqusShortname?: string;
  post: WP_REST_API_Post & {
    next: WP_NextPrev | null;
    previous: WP_NextPrev | null;
  };
  categories: WP_REST_API_Categories;
  posts: WP_REST_API_Post[];
};

/* TODO: TEMPORAL helper. Investigate to use domutils, html-to-text, etc */
function nodeText(node: any): string {
  if (node.type === 'text') return node.data ?? '';
  if (node.children) return node.children.map(nodeText).join('');
  return '';
}

const BlogPostComponent: React.FC<BlogPostComponentProps> = ({
  post,
  categories,
  posts
}) => {
  const anchorsRef = useRef<Anchor[]>([]);
  const [modalSrc, setModalSrc] = useState<string | null>(null);
  const [shareModalIsOpen, setIsOpen] = useState(false);
  const { t } = useI18n('blog');
  const { locale } = useRouter();
  const { user, loadingUser } = useContext(LoggedUserContext);
  const [indexOpen, setIndexOpen] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const openDrawer = () => setDrawerOpen(true);
  const closeDrawer = () => setDrawerOpen(false);

  function closeImgModal(): void {
    setModalSrc(null);
  }

  function openShareModal(): void {
    setIsOpen(true);
  }

  function closeShareModal(): void {
    setIsOpen(false);
  }

  const postContent = useMemo(() => {
    anchorsRef.current = [];
    const options: HTMLReactParserOptions = {
      replace: (domNode) => {
        if (domNode.type === 'tag') {
          const el = domNode as Element;
          if (el.name === 'h2') {
            const text = nodeText(el).trim();
            const id = slugify(text);

            el.attribs = { ...el.attribs, id };
            anchorsRef.current.push({ headerLevel: 2, title: text, id });
          }
          if (el.name === 'img' && el.attribs?.src) {
            const { src, alt } = el.attribs;
            return (
              <img
                {...el.attribs}
                style={{ cursor: 'zoom-in' }}
                onClick={() => setModalSrc(src)}
                alt={alt}
              />
            );
          }
        }
        // @ts-expect-error don't know why, but I cannot use `domNode instanceof Element`, as stated here: https://www.npmjs.com/package/html-react-parser#migration
        if (domNode.attribs?.class === 'signup-banner') {
          return <>{!user && !loadingUser && <BlogRegisterCta />}</>;
        }
      }
    };
    return parse(post.content.rendered, options);
  }, []);

  const sortedCategories = useMemo(
    () => sortCategories(categories),
    [categories]
  );

  return (
    <Page hasScrollTop>
      <StyledContainer>
        <StyledHeaderWrapper>
          <PostHeaderCard post={post} sortedCategories={sortedCategories} />
        </StyledHeaderWrapper>
        <StyledMainLayout>
          <StyledPostIndexWrapper>
            <StyledPostIndexSticky>
              <PostIndex post={post} />
            </StyledPostIndexSticky>
          </StyledPostIndexWrapper>
          <StyledPostContentWrapper>
            <StyledPostContent>{postContent}</StyledPostContent>
            <StyledPostInfoFooter>
              <Button sizes="sm" variant="tertiary" onClick={openShareModal}>
                <FontAwesomeIcon icon={faShare} />
                <span>{t('post.share')}</span>
              </Button>
            </StyledPostInfoFooter>
          </StyledPostContentWrapper>
        </StyledMainLayout>
        <StyledRecommendedPosts
          currentPost={post}
          categories={categories}
          locale={locale}
        />

        <BlogCta />
        <StyledFloatingBannerContainer>
          <BlogFloatingCta />
        </StyledFloatingBannerContainer>
      </StyledContainer>
      <BlogShareModal
        slug={post.slug}
        shareModalIsOpen={shareModalIsOpen}
        shareModalClose={closeShareModal}
      />
      <ImageModal
        isOpen={Boolean(modalSrc)}
        src={modalSrc}
        onClose={closeImgModal}
      />
      <IndexToggleButton
        $isOpen={drawerOpen}
        label=""
        onClick={() => (drawerOpen ? closeDrawer() : openDrawer())}
        schema={'neutral'}
        startIcon={faList}
      />

      {drawerOpen && (
        <DrawerOverlay onClick={closeDrawer}>
          <IndexDrawer open={drawerOpen} onClick={(e) => e.stopPropagation()}>
            <DrawerCloseButton onClick={closeDrawer}>×</DrawerCloseButton>
            <IndexDrawerContent onClick={closeDrawer}>
              <PostIndex post={post} />
            </IndexDrawerContent>
          </IndexDrawer>
        </DrawerOverlay>
      )}
    </Page>
  );
};

export default BlogPostComponent;
